import React from "react";
import { formatNumber } from "../format";
import { StatCard, ListRow } from "./shared";
import { DeliveryNoteCard } from "./deliveryNotes";

const predictionBucket = (source) => ({
  vehicles: Number(source?.vehicles) || 0,
  serviceCount: Number(source?.serviceCount) || 0,
  potential: Number(source?.potential) || 0,
  items: source?.items || [],
});

const CompletionInfo = ({ pending, completed, translate, money }) => (
  <div className="fleetbot-info-tip">
    <div className="fleetbot-info-tip-block">
      <strong>{translate("notCompleted")}</strong>
      <div>
        {formatNumber(pending.vehicles)} ·{" "}
        {formatNumber(pending.serviceCount)} {translate("fleetBotTotalServices")}
      </div>
      <div>{money(pending.potential)}</div>
    </div>
    <div className="fleetbot-info-tip-block">
      <strong>{translate("completed")}</strong>
      <div>
        {formatNumber(completed.vehicles)} ·{" "}
        {formatNumber(completed.serviceCount)} {translate("fleetBotTotalServices")}
      </div>
      <div>{money(completed.potential)}</div>
    </div>
  </div>
);

const AiPmsStatCard = ({ label, source = {}, translate, money, accent }) => {
  const pending = predictionBucket(source.pending || source);
  const completed = predictionBucket(source.completed);
  return (
    <StatCard
      label={label}
      value={formatNumber(source.vehicles)}
      hint={`${translate("notCompleted")} ${formatNumber(
        pending.vehicles
      )} · ${translate("completed")} ${formatNumber(completed.vehicles)}`}
      accent={accent}
      info={
        <CompletionInfo
          pending={pending}
          completed={completed}
          translate={translate}
          money={money}
        />
      }
    />
  );
};

const UpcomingMeters = ({ services, translate }) => {
  if (!services?.length) return null;
  const max = Math.max(...services.map((item) => item.count), 1);
  return (
    <>
      <div className="fleetbot-section-title">
        {translate("upcomingMaintenance")}
      </div>
      <div className="fleetbot-list">
        {services.map((item) => {
          const pct = Math.round((item.count / max) * 100);
          return (
            <div key={item.name} className="fleetbot-meter-row">
              <div className="fleetbot-meter-label">
                <span>{item.name}</span>
                <strong>{formatNumber(item.count)}</strong>
              </div>
              <div className="fleetbot-meter">
                <span style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

const AiPmsList = ({ title, items, translate, money }) => {
  if (!items?.length) return null;
  return (
    <>
      <div className="fleetbot-section-title">{title}</div>
      <div className="fleetbot-list">
        {items.map((item, index) => (
          <ListRow
            key={`${title}-${item.completed}-${item.plateNumber}-${index}`}
            title={item.plateNumber || "—"}
            meta={`${formatNumber(item.count)} ${translate(
              "fleetBotTotalServices"
            )}`}
            value={item.totalPotential ? money(item.totalPotential) : null}
            badge={
              Number(item.completed) === 1
                ? translate("completed")
                : translate("notCompleted")
            }
            badgeTone={Number(item.completed) === 1 ? "" : "is-warn"}
          />
        ))}
      </div>
    </>
  );
};

export function renderServices(questionId, data, { translate, money }) {
  if (questionId === "servicesThisMonth") {
    const month = data.month || {};
    const upcoming = month.upcoming || {};
    const due = month.aiPmsDue || {};
    const schedule = month.aiPmsSchedule || {};

    return (
      <>
        <div className="fleetbot-stats">
          <StatCard
            label={translate("upcomingMaintenance")}
            value={formatNumber(upcoming.vehiclesAffected)}
            hint={translate("next30Days")}
            accent
          />
          <AiPmsStatCard
            label={translate("AI PMS Due")}
            source={due}
            translate={translate}
            money={money}
          />
          <AiPmsStatCard
            label={translate("AI PMS Schedule")}
            source={schedule}
            translate={translate}
            money={money}
          />
          <StatCard
            label={translate("totalPotential")}
            value={money(
              (Number(due.potential) || 0) + (Number(schedule.potential) || 0)
            )}
          />
        </div>
        <UpcomingMeters services={upcoming.services} translate={translate} />
        <AiPmsList
          title={translate("AI PMS Due")}
          items={due.items}
          translate={translate}
          money={money}
        />
        <AiPmsList
          title={translate("AI PMS Schedule")}
          items={schedule.items}
          translate={translate}
          money={money}
        />
      </>
    );
  }

  if (questionId === "upcomingServices") {
    const upcoming = data.upcoming || {};
    const schedule = data.aiPmsSchedule || {};

    return (
      <>
        <div className="fleetbot-stats">
          <StatCard
            label={translate("upcomingMaintenance")}
            value={formatNumber(upcoming.vehiclesAffected)}
            hint={translate("next30Days")}
            accent
          />
          <AiPmsStatCard
            label={translate("AI PMS Schedule")}
            source={schedule}
            translate={translate}
            money={money}
          />
          <StatCard
            label={translate("totalPotential")}
            value={money(schedule.potential)}
          />
        </div>
        <UpcomingMeters services={upcoming.services} translate={translate} />
        <AiPmsList
          title={translate("AI PMS Schedule")}
          items={schedule.items}
          translate={translate}
          money={money}
        />
      </>
    );
  }

  if (questionId === "overdueServices") {
    const due = data.aiPmsDue || {};
    const pending = predictionBucket(due.pending || due);
    const completed = predictionBucket(due.completed);

    return (
      <>
        <div className="fleetbot-stats">
          <AiPmsStatCard
            label={translate("AI PMS Due")}
            source={due}
            translate={translate}
            money={money}
            accent
          />
          <StatCard
            label={translate("notCompleted")}
            value={formatNumber(pending.vehicles)}
            hint={`${formatNumber(pending.serviceCount)} ${translate(
              "fleetBotTotalServices"
            )}`}
          />
          <StatCard
            label={translate("completed")}
            value={formatNumber(completed.vehicles)}
            hint={`${formatNumber(completed.serviceCount)} ${translate(
              "fleetBotTotalServices"
            )}`}
          />
          <StatCard
            label={translate("totalPotential")}
            value={money(due.potential)}
          />
        </div>
        <AiPmsList
          title={translate("AI PMS Due")}
          items={due.items}
          translate={translate}
          money={money}
        />
      </>
    );
  }

  if (questionId === "serviceHistory") {
    const notes = data.list || [];
    const summary = data.summary || {};

    return (
      <>
        {summary.label ? (
          <div className="fleetbot-section-title">{summary.label}</div>
        ) : null}
        <div className="fleetbot-stats">
          <StatCard
            label={translate("fleetBotTotalDeliveryNotes")}
            value={formatNumber(summary.totalDeliveryNotes || notes.length)}
            accent
          />
          <StatCard
            label={translate("fleetBotTotalAmount")}
            value={money(summary.totalAmount)}
          />
          <StatCard
            label={translate("fleetBotInvoicedAmount")}
            value={money(summary.invoicedAmount)}
          />
          <StatCard
            label={translate("fleetBotNonInvoicedAmount")}
            value={money(summary.nonInvoicedAmount)}
          />
        </div>
        <div className="fleetbot-list">
          {notes.map((item) => (
            <DeliveryNoteCard
              key={item.id || item.deliveryNoteNumber}
              item={item}
              translate={translate}
              money={money}
              showPlate
            />
          ))}
        </div>
      </>
    );
  }

  return null;
}
